import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "../assets/custom_styles.css";
import barchart from "../assets/img/Barchart.png";
import linechart from "../assets/img/Linechart.png";
import table_img from "../assets/img/table_img.png";
import OvertimeBarchart from "./datavisualization/CancerOvertime/Barchart";
import OvertimeLinechart from "./datavisualization/CancerOvertime/Linechart";
import Select from "react-select";
import DataTable from "react-data-table-component";

// Imports for export as pdf,csv,excel
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import jsPDF from "jspdf";
import "jspdf-autotable";
// import { exportToPDF } from "./datavisualization/exportUtils";

/** Functions for export as pdf,csv and excel */

const exportToExcel = (data, title = "Exported Data", filename = "data") => {
  if (!data || data.length === 0) return;

  // 1. Extract headers
  const originalHeaders = Object.keys(data[0]);
  const headers = originalHeaders.map((h) => h.toUpperCase());

  // 2. Convert data to 2D array
  const rows = data.map((row) => originalHeaders.map((header) => row[header]));

  // 3. Combine title + headers + rows
  const aoa = [
    [title], // Title row
    [], // Spacer row (optional)
    headers, // Header row
    ...rows, // Data rows
  ];

  // 4. Create worksheet
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);

  // 5. Add merge for the title row across all columns
  worksheet["!merges"] = [
    {
      s: { r: 0, c: 0 }, // Start at A1
      e: { r: 0, c: headers.length - 1 }, // End at last column of row 1
    },
  ];

  // 6. Create workbook and export
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sheet1");

  const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
  const blob = new Blob([excelBuffer], { type: "application/octet-stream" });
  saveAs(blob, `${filename}.xlsx`);
};

const exportToCSV = (data, title, filename = "data") => {
  if (!data || data.length === 0) return;

  // 1️. Extract headers
  const originalHeaders = Object.keys(data[0]);
  const headers = originalHeaders.map((h) => {
    const upper = h.toUpperCase();
    //Convert to number pattern instead of date
    return /^\d+\s*-\s*\d+$/.test(upper) ? `\t${upper}` : upper;
  });
  // 2️. Create rows (same as Excel)
  const rows = data.map((row) => originalHeaders.map((header) => row[header]));

  // 3️. Combine title + headers + rows
  const aoa = [[title], [], headers, ...rows];

  // 4️. Convert AOA to worksheet, then to CSV
  const worksheet = XLSX.utils.aoa_to_sheet(aoa);
  worksheet["!merges"] = [
    {
      s: { r: 0, c: 0 }, // Start at A1
      e: { r: 0, c: headers.length - 1 }, // End at last column of row 1
    },
  ];
  const csv = XLSX.utils.sheet_to_csv(worksheet);

  // 5️. Create and download CSV blob
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  saveAs(blob, `${filename}.csv`);
};

const PDFExport = ({ data, columns }) => {
  const handleExport = () => {
    const doc = new jsPDF();

    doc.autoTable({
      head: [columns.map((col) => col.name)],
      body: data.map((row) =>
        columns.map((col) =>
          typeof col.selector === "function"
            ? col.selector(row)
            : row[col.selector],
        ),
      ),
    });

    doc.save("table.pdf");
  };
  return <button onClick={handleExport}>Export to PDF</button>;
};
/*********************************************/

const numericColumnAccessors = new Set([
  "year",
  "total_male_cases",
  "total_female_cases",
  "total_cases",
  "total_male_cir",
  "total_female_cir",
  "total_cir",
  "total_male_asr",
  "total_female_asr",
  "total_asr",
]);

const getSortableValue = (value, isNumeric) => {
  if (!isNumeric) return value;

  if (value === null || value === undefined || value === "") {
    return Number.NEGATIVE_INFINITY;
  }

  if (typeof value === "number") {
    return value;
  }

  const normalized = String(value).replace(/,/g, "").trim();
  const parsedValue = Number.parseFloat(normalized.replace(/[%\s]/g, ""));

  return Number.isNaN(parsedValue) ? Number.NEGATIVE_INFINITY : parsedValue;
};

const compareSortableValues = (a, b, direction, isNumeric) => {
  if (!isNumeric) {
    return direction === "asc"
      ? String(a).localeCompare(String(b))
      : String(b).localeCompare(String(a));
  }

  const aValue = getSortableValue(a, true);
  const bValue = getSortableValue(b, true);

  if (aValue === bValue) return 0;
  return direction === "asc" ? aValue - bValue : bValue - aValue;
};

const CancerOvertime = () => {
  const display = [
    { label: "By place", value: "population" },
    { label: "By cancer", value: "Cancer" },
  ];
  const [selectedDisplay, setSelectedDisplay] = useState({
    label: "By place",
    value: "population",
  });
  const [cancerList, setCancerList] = useState([]);
  const [selectedCancers, setSelectedCancers] = useState([]);
  const genderOptions = [
    { value: "1", label: "Male" },
    { value: "2", label: "Female" },
    { value: "0", label: "Both" },
  ];
  const [selectedGenders, setSelectedGenders] = useState({
    value: "0",
    label: "Both",
  });
  const [districtList, setDistrictList] = useState([]);
  const [selectedDistricts, setSelectedDistricts] = useState([]);
  const fromAge = [
    { value: 0, label: "0 - 4" },
    { value: 5, label: "5 - 9" },
    { value: 10, label: "10 - 14" },
    { value: 15, label: "15 - 19" },
    { value: 20, label: "20 - 24" },
    { value: 25, label: "25 - 29" },
    { value: 30, label: "30 - 34" },
    { value: 35, label: "35 - 39" },
    { value: 40, label: "40 - 44" },
    { value: 45, label: "45 - 49" },
    { value: 50, label: "50 - 54" },
    { value: 55, label: "55 - 59" },
    { value: 60, label: "60 - 64" },
    { value: 65, label: "65 - 69" },
    { value: 70, label: "70 - 74" },
    { value: 75, label: "75+" },
  ];
  const toAge = [
    { value: 4, label: "0 - 4" },
    { value: 9, label: "5 - 9" },
    { value: 14, label: "10 - 14" },
    { value: 19, label: "15 - 19" },
    { value: 24, label: "20 - 24" },
    { value: 29, label: "25 - 29" },
    { value: 34, label: "30 - 34" },
    { value: 39, label: "35 - 39" },
    { value: 44, label: "40 - 44" },
    { value: 49, label: "45 - 49" },
    { value: 54, label: "50 - 54" },
    { value: 59, label: "55 - 59" },
    { value: 64, label: "60 - 64" },
    { value: 69, label: "65 - 69" },
    { value: 74, label: "70 - 74" },
    { value: 75, label: "75+" },
  ];
  const [selectedFromAge, setSelectedFromAge] = useState(null);
  const [selectedToAge, setSelectedToAge] = useState(null);
  const rateOptions = [
    { value: "cases", label: "Number of Cases" },
    { value: "cir", label: "Crude Incidence Rate(CIR)" },
    { value: "asr", label: "Age Standardized Rate(ASR)" },
  ];
  const [selectedRate, setSelectedRate] = useState({
    value: "cases",
    label: "Number of Cases",
  });
  const [showModal, setShowModal] = useState(false);
  const [validationMessage, setValidationMessage] = useState("");
  const [tableData, setTableData] = useState([]);
  const [title, setTitle] = useState("");
  const [subtitle1, setSubtitle1] = useState("");
  const [subtitle2, setSubtitle2] = useState("");
  const projectionYear = Number(sessionStorage.getItem("projection_year"));
  const [loading, setLoading] = useState(true);
  const [columns, setColumns] = useState([]); //To set dynamic columns
  const [selectedChart, setSelectedChart] = useState(null);
  const [chartData, setChartData] = useState({});
  const [hasValidationError, setHasValidationError] = useState(false);
  const isNoData =
    !loading &&
    !hasValidationError &&
    (Array.isArray(tableData) ? tableData.length === 0 : !tableData);
  const [footNote, setFootNote] = useState("");

  // District footnote mapping - customize messages based on district ID
  const districtFootnoteMap = {
    1: " Chennai  \u2013   includes 155 divisions covering 170 km\u00B2",
    2: " Kanchipuram  \u2013  includes Chengalpattu district + part of GCC areas",
    3: " Tiruvallur \u2013  includes part of GCC areas",
    25: " Nagapattinam  \u2013  includes Mayiladuthurai district",
    4: " Viluppuram \u2013   includes Kallakurichi district",
    7: " Vellore \u2013  includes Ranipet and Tirupattur districts",
    27: " Tirunelveli \u2013  includes Tenkasi district",
  };

  // Function to generate dynamic footnote message based on selected districts
  const generateFootnoteMessage = () => {
    const prefix = "Old district boundaries as per 2011 census; ";

    if (!selectedDistricts || selectedDistricts.length === 0) {
      return "";
    }

    const customFootnotes = selectedDistricts
      .map((district) => {
        const districtId = Number(district.id);
        return districtFootnoteMap[districtId];
      })
      .filter(Boolean);

    return customFootnotes.length > 0
      ? prefix + customFootnotes.join("; ")
      : "";
  };
  const shouldShowDistrictFootnote = () => {
    return selectedDistricts.length > 0;
  };
  const cancelTokenSourceRef = useRef(null);
  const customStyles = {
    headRow: {
      style: {
        minHeight: "32px",
      },
    },
    headCells: {
      style: {
        backgroundColor: " #007bff",
        color: "#fff",
        fontSize: "14px",
        fontWeight: "600",
        padding: "6px 10px",
        justifyContent: "center",
        textAlign: "center",
        border: "1px solid #dee2e6",
        whiteSpace: "nowrap",
      },
    },
    cells: {
      style: {
        border: "1px  #dee2e6",
        borderStyle: "inset",
      },
    },
  };
  const onChangeFilter = () => {
    const cancerChange = selectedCancers
      .map((filterItem) => {
        const matchCancer = cancerList.find(
          (d) => d.value === filterItem.value,
        );
        const matchResult = matchCancer
          ? { name: filterItem.value, gender: matchCancer.gender }
          : null;
        return matchResult;
      })
      .filter(Boolean);

    let hasMale = false;
    let hasFemale = false;
    let hasBoth = false;

    for (const { name } of cancerChange) {
      const matchCancer = cancerList.find((d) => d.value === name);

      if (matchCancer) {
        const gender = Number(matchCancer.gender); // type conversion string into a number

        if (gender === 0) hasBoth = true;
        if (gender === 1) hasMale = true;
        if (gender === 2) hasFemale = true;
      }
    }
    let genderResult;
    if (hasBoth && hasMale && hasFemale) {
      genderResult = "Both"; // all present
    } else if (hasBoth && hasMale) {
      genderResult = "Male"; // 0 & 1
    } else if (hasBoth && hasFemale) {
      genderResult = "Female"; // 0 & 2
    } else if (hasMale && hasFemale) {
      genderResult = "Both"; //1 & 2
    } else if (hasMale) {
      genderResult = "Male"; // only 1
    } else if (hasFemale) {
      genderResult = "Female"; // only 2
    } else if (hasBoth) {
      genderResult = "Both"; // only 0
    } else {
      genderResult = "Both";
    }
    return genderResult;
  };
  const handleDisplayChange = (val) => {
    setSelectedDisplay(val);
  };
  const handleCancerChange = (val) => {
    setSelectedCancers(val || []);
    setSelectedGenders({
      value: "0",
      label: "Both",
    }); // reset gender filter
  };

  const handleGenderChange = (selectedGender) => {
    setSelectedGenders(selectedGender); //returns single Object
  };
  const validateRange = (fromAge, toAge) => {
    if (
      fromAge?.value !== undefined &&
      toAge?.value !== undefined &&
      fromAge.value > toAge.value
    ) {
      setValidationMessage("From Age must be less than To Age.");
      setShowModal(true);
      setHasValidationError(true);
      setLoading(false);
      setTableData([]);
      setColumns([]);
      cancelTokenSourceRef.current?.cancel();
      return false;
    }

    setShowModal(false);
    setHasValidationError(false);
    return true;
  };
  const handleFromAgeChange = (val) => {
    setSelectedFromAge(val);
    validateRange(val, selectedToAge);
  };

  const handleToAgeChange = (val) => {
    setSelectedToAge(val);
    validateRange(selectedFromAge, val);
  };

  const handleRateChange = (selectedRate) => {
    setSelectedRate(selectedRate);
  };

  useEffect(() => {
    const genderLabel = onChangeFilter(); // returns 'Male', 'Female', or 'Both'

    // Find the gender option that matches the label returned
    const selectedGenderOption = genderOptions.find(
      (opt) => opt.label === genderLabel,
    ) || { value: "0", label: "Both" };
    setSelectedGenders(selectedGenderOption);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCancers]);

  useEffect(() => {
    sessionStorage.setItem("visual_type", JSON.stringify("overtime"));
  }, []);
  useEffect(() => {
    let isMounted = true;

    // Prevent fetching data when validation rules are violated.
    // This ensures the UI doesn't show "no data" while the user is still correcting the input.
    const isValidRange = validateRange(selectedFromAge, selectedToAge);

    if (!isValidRange) {
      setLoading(false);
      return () => {
        isMounted = false;
      };
    }
    const fetchCancerOvertime = async () => {
      setLoading(true);
      if (!validateRange(selectedFromAge, selectedToAge)) {
        return;
      }

      // Cancel any previous request before starting a new one
      cancelTokenSourceRef.current?.cancel();
      cancelTokenSourceRef.current = axios.CancelToken.source();

      try {
        const analyticData = {
          district:
            selectedDistricts.length > 0
              ? selectedDistricts.map((district) => district.id)
              : "all",
          cancer_type:
            selectedCancers.length > 0
              ? selectedCancers.map((c) => c.id)
              : "all",

          gender:
            selectedGenders?.value === "0"
              ? "both"
              : parseInt(selectedGenders?.value, 10), // Converts "1"/"2" to 1/2
          from_age_range: selectedFromAge ? selectedFromAge.value : 0,
          to_age_range: selectedToAge ? selectedToAge.value : 75,
          from_year: 2012,
          to_year: 2016,
          visual_type: "overtime",
          display_type: "table",
          visual_mode:
            selectedDisplay?.value === "population"
              ? "population"
              : selectedDisplay?.value === "Cancer"
                ? "Cancer"
                : null,
          by_value: selectedRate.value || "cases",
        };
        const display_type = selectedDisplay?.label || "Population";
        const gender = selectedGenders?.label || "Both";
        const fromAge = selectedFromAge?.value || 0;
        const toAge =
          selectedToAge?.value === 75 ? "75+" : selectedToAge?.value || "75+";
        const rateLabel = selectedRate?.label || "Number of Cases";
        setTitle(
          `Trend of Cancer Incidence,${rateLabel} ${display_type}, Gender is ${gender} and age group ${fromAge} - ${toAge}, from 2012 to ${projectionYear} `,
        );

        const cancerNames = selectedCancers
          .map((c) => c.label || c.name)
          .join(", ");
        const districtNames = selectedDistricts
          .map((d) => d.label || d.name)
          .join(", ");

        setSubtitle1(cancerNames || "All Cancer Types");
        setSubtitle2(districtNames || "All Districts");
        const token = sessionStorage.getItem("token");
        const [cancerListRes, districtListRes, AnalyticalDataRes] =
          await Promise.all([
            // API Call: Display_CancerList
            // Purpose: Retrieves a list of all available cancer types from the database
            // Returns: Array of cancer objects containing cancer_master_id, cancer_code, cancer_name, and gender information
            // Used for: Populating the cancer type dropdown filter in the UI
            axios.get("Display_CancerList", { withCredentials: false }),
            // API Call: Display_DistrictList
            // Purpose: Retrieves a list of all available districts/geographical regions from the database
            // Returns: Array of district objects containing district_master_id and district_name
            // Used for: Populating the district/location dropdown filter in the UI
            axios.get("Display_DistrictList", { withCredentials: false }),
            // API Call: /getAnalyticalData
            // Purpose: Fetches historical cancer data over time based on user-selected filters (cancer type, district, gender, age range, year range)
            // Method: POST (sends filter criteria to the server)
            // Parameters: analyticData object containing district, cancer_type, gender, age range, year range, visual_type (overtime), display_type, visual_mode
            // Returns: Processed historical data including case counts by year and gender for trend analysis
            // Authentication: Uses Bearer token from session storage for secure access
            // Used for: Populating time-series charts and tables showing cancer incidence trends over multiple years
            axios.post("getAnalyticalData", analyticData, {
              headers: {
                Authorization: `Bearer ${token}`, // Send token in headers
                "Content-Type": "application/json",
              },
              cancelToken: cancelTokenSourceRef.current.token,
            }),
          ]);
        //Fetch the Cancer and District list from API and map it in the  filter dropdown
        const allCancer = cancerListRes.data.cancer_list;
        const cancerType = allCancer.map((cancer) => ({
          id: cancer.cancer_master_id,
          label: `${cancer.cancer_code} - ${cancer.cancer_name}`,
          value: cancer.cancer_code,
          gender: cancer.gender,
        }));
        const allDistricts = districtListRes.data;
        const districts = allDistricts.map((district) => ({
          id: district.district_master_id,
          label: district.district_name,
          value: district.district_name,
        }));
        setCancerList(cancerType);
        setDistrictList(districts);

        //Fetch the Analytic data from the API
        const responseData = AnalyticalDataRes.data.data;

        // Determine which column mapping to use based on selected rate
        let displayColumnMap = {};

        if (selectedRate.value === "cases") {
          displayColumnMap = {
            both: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Male",
                accessor: "total_male_cases",
                align: "center",
              },
              {
                header: "Female",
                accessor: "total_female_cases",
                align: "center",
              },
              {
                header: "Total",
                accessor: "total_cases",
                align: "center",
              },
            ],
            1: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Male",
                accessor: "total_male_cases",
                align: "center",
              },
            ],
            2: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Female",
                accessor: "total_female_cases",
                align: "center",
              },
            ],
          };
        } else if (selectedRate.value === "cir") {
          displayColumnMap = {
            both: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Male CIR",
                accessor: "total_male_cir",
                align: "center",
              },
              {
                header: "Female CIR",
                accessor: "total_female_cir",
                align: "center",
              },
              // {
              //   header: "Total CIR",
              //   accessor: "total_cir",
              //   align: "center",
              // },
            ],
            1: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Male CIR",
                accessor: "total_male_cir",
                align: "center",
              },
            ],
            2: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Female CIR",
                accessor: "total_female_cir",
                align: "center",
              },
            ],
          };
        } else if (selectedRate.value === "asr") {
          displayColumnMap = {
            both: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Male ASR",
                accessor: "total_male_asr",
                align: "center",
              },
              {
                header: "Female ASR",
                accessor: "total_female_asr",
                align: "center",
              },
              // {
              //   header: "Total ASR",
              //   accessor: "total_asr",
              //   align: "center",
              // },
            ],
            1: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Male ASR",
                accessor: "total_male_asr",
                align: "center",
              },
            ],
            2: [
              { header: "Year", accessor: "year", align: "center" },
              {
                header: "Female ASR",
                accessor: "total_female_asr",
                align: "center",
              },
            ],
          };
        }

        const selectedKey =
          selectedGenders?.value === "0" ? "both" : selectedGenders?.value;

        const finalColumns = (displayColumnMap[selectedKey] || []).map(
          (col) => ({
            name: col.header,
            selector: (row) => {
              const value = row[col.accessor];
              return getSortableValue(
                value,
                numericColumnAccessors.has(col.accessor),
              );
            },
            sortable: true,
            sortFunction: (rowA, rowB, direction) =>
              compareSortableValues(
                rowA[col.accessor],
                rowB[col.accessor],
                direction,
                numericColumnAccessors.has(col.accessor),
              ),
            cell: (row) => (
              <div style={{ textAlign: col.align || "center", width: "100%" }}>
                {row[col.accessor]}
              </div>
            ),
          }),
        );

        // Format data based on selected rate and gender
        const formattedData = responseData.map((item) => {
          if (selectedRate.value === "cases") {
            if (selectedKey === "both") {
              return {
                year: item.year,
                total_male_cases: item.total_male_cases,
                total_female_cases: item.total_female_cases,
                total_cases: item.total_cases,
              };
            } else if (selectedKey === "1") {
              return {
                year: item.year,
                total_male_cases: item.total_male_cases,
              };
            } else if (selectedKey === "2") {
              return {
                year: item.year,
                total_female_cases: item.total_female_cases,
              };
            }
          } else if (selectedRate.value === "cir") {
            if (selectedKey === "both") {
              return {
                year: item.year,
                total_male_cir: item.total_male_cir,
                total_female_cir: item.total_female_cir,
                total_cir: item.total_cir,
              };
            } else if (selectedKey === "1") {
              return {
                year: item.year,
                total_male_cir: item.total_male_cir,
              };
            } else if (selectedKey === "2") {
              return {
                year: item.year,
                total_female_cir: item.total_female_cir,
              };
            }
          } else if (selectedRate.value === "asr") {
            if (selectedKey === "both") {
              return {
                year: item.year,
                total_male_asr: item.total_male_asr,
                total_female_asr: item.total_female_asr,
                total_asr: item.total_asr,
              };
            } else if (selectedKey === "1") {
              return {
                year: item.year,
                total_male_asr: item.total_male_asr,
              };
            } else if (selectedKey === "2") {
              return {
                year: item.year,
                total_female_asr: item.total_female_asr,
              };
            }
          }
          return null;
        });

        const chartdetails = responseData;
        if (isMounted) {
          setTableData(formattedData);
          setColumns(finalColumns);
          setChartData(chartdetails);
          setLoading(false);
        }
      } catch (err) {
        if (!axios.isCancel(err)) {
          console.error("Error fetching Cancer Overtime data:", err);
        }
      } finally {
      }
    };
    const debounceTimer = setTimeout(() => {
      fetchCancerOvertime();
    }, 500); // 500ms delay

    return () => {
      isMounted = false;
      clearTimeout(debounceTimer);
    };
    // fetchCancerOvertime();
  }, [
    selectedDisplay,
    selectedCancers,
    selectedGenders,
    selectedDistricts,
    selectedFromAge,
    selectedToAge,
    selectedRate,
    selectedChart,
  ]);

  useEffect(() => {
    const footnoteMessage = shouldShowDistrictFootnote()
      ? generateFootnoteMessage()
      : "";

    setFootNote(footnoteMessage);
  }, [selectedDistricts, selectedDisplay, selectedCancers]);
  return (
    <div className="container">
      <div className="row mt-3">
        {/* //Filter Design */}
        <div className="col-12 col-md-4 mb-3">
          <div className="sticky-filters ">
            <div className="shadow pt-1 mb-2 bg-white rounded-4 d-flex align-items-center justify-content-evenly">
              <img
                className={`chart-icon px-2 py-2 ${
                  selectedChart === "bar" ? "active" : ""
                }`}
                src={barchart}
                alt="Barchart"
                onClick={() => {
                  setSelectedChart("bar");
                }}
              ></img>
              <img
                className={`chart-icon px-2 py-2 ${
                  selectedChart === "line" ? "active" : ""
                }`}
                src={linechart}
                alt="Linechart"
                onClick={() => {
                  setSelectedChart("line");
                }}
              ></img>
              <img
                className={`chart-icon px-2 py-2 ${
                  selectedChart === "table" ? "active" : ""
                } `}
                src={table_img}
                alt="table"
                onClick={() => {
                  setSelectedChart("table");
                }}
              ></img>
            </div>

            <div>
              <div className="shadow pt-4 px-4 pb-2  mb-2 bg-white rounded-4">
                <div className="row align-items-center">
                  <p className="col-12 fs-4 ">Trend of Cancer Incidence Data</p>
                </div>
                <form>
                  <div className="mb-3">
                    <label id="display" className="mb-2">
                      Select
                    </label>
                    <div>
                      <Select
                        type="checkbox"
                        aria-labelledby="display"
                        options={display}
                        value={selectedDisplay}
                        onChange={handleDisplayChange}
                        closeMenuOnSelect={true}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="--Select--"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label id="Cancer_type" className="mb-2">
                      Site of Cancer
                    </label>
                    <div>
                      <Select
                        isMulti
                        aria-labelledby="Cancer_type"
                        options={cancerList}
                        value={selectedCancers}
                        onChange={handleCancerChange}
                        closeMenuOnSelect={true}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="All Cancers Together"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label id="Gender" className="mb-2">
                      Gender
                    </label>
                    <div>
                      <Select
                        aria-labelledby="Gender"
                        options={genderOptions}
                        value={selectedGenders}
                        onChange={handleGenderChange}
                        closeMenuOnSelect={true}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="--Select--"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label id="district_name" className="mb-2">
                      Residence District
                    </label>
                    <div>
                      <Select
                        isMulti
                        aria-labelledby="district_name"
                        options={districtList}
                        value={selectedDistricts}
                        onChange={setSelectedDistricts}
                        closeMenuOnSelect={true}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="All districts together"
                      />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label id="age_group" className="form-label mb-2">
                      Age at Diagnosis
                    </label>
                    <div className="row">
                      <Select
                        className="col-6"
                        aria-labelledby="age_group"
                        options={fromAge}
                        value={selectedFromAge}
                        onChange={handleFromAgeChange}
                        placeholder="From"
                      />
                      <Select
                        className="col-6"
                        aria-labelledby="age_group"
                        options={toAge}
                        value={selectedToAge}
                        onChange={handleToAgeChange}
                        placeholder="To"
                      />
                    </div>
                  </div>
                  <div className="mb-3">
                    <label id="rate" className="mb-2">
                      Rates
                    </label>
                    <div>
                      <Select
                        aria-labelledby="rate"
                        options={rateOptions}
                        value={selectedRate}
                        onChange={handleRateChange}
                        closeMenuOnSelect={true}
                        className="basic-multi-select"
                        classNamePrefix="select"
                        placeholder="All Rates"
                      />
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>

        {/* Renders Dynamic Chart */}
        <div className="col-12 col-md-8 col-lg-8 content-scroll">
          <h2 className="text-center fs-4 mb-3 text-primary">{title}</h2>
          <h6 className="text-center fs-5 mb-3 text-primary">{subtitle1}</h6>
          <h6 className="text-center fs-5 mb-3 text-primary">{subtitle2}</h6>
          {showModal && (
            <div className="modal show d-block" tabIndex="-1" role="dialog">
              <div className="modal-dialog" role="document">
                <div className="modal-content">
                  <div className="modal-header bg-primary text-white">
                    <h5 className="modal-title"> Error!</h5>
                  </div>
                  <div className="modal-body">
                    <p>{validationMessage}</p>
                  </div>
                  <div className="modal-footer">
                    <button
                      className="btn btn-secondary"
                      onClick={() => setShowModal(false)}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
          {!selectedChart && (
            <div>
              {hasValidationError || isNoData ? (
                <div className="text-center mt-5 text-muted">
                  <p className="fs-5">No data found</p>
                </div>
              ) : loading ? (
                <div className="d-flex justify-content-center">
                  <div
                    className="spinner-border center text-primary"
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                // Export Buttons
                <div>
                  <div className="d-flex justify-content-end mb-3">
                    <button
                      className="btn btn-outline-success me-2"
                      onClick={() =>
                        exportToExcel(
                          tableData,
                          title,
                          "tncrp_visualizationdata",
                        )
                      }
                    >
                      <i className="bi bi-file-earmark-excel me-1"></i>
                    </button>

                    <button
                      className="btn btn-outline-primary me-2"
                      onClick={() => exportToCSV(tableData)}
                      title="Export as CSV"
                    >
                      <i className="bi bi-file-earmark-text"></i>
                    </button>
                  </div>
                  <DataTable
                    columns={columns}
                    data={tableData}
                    customStyles={customStyles}
                    fixedHeader // freezes the header
                    fixedHeaderScrollHeight="600px" //table body scroll area
                    pagination
                    paginationPerPage={100}
                    highlightOnHover
                    dense
                    striped
                    responsive
                  ></DataTable>
                  {footNote && <p>{footNote}</p>}
                </div>
              )}
            </div>
          )}
          {selectedChart === "table" && (
            <div>
              {hasValidationError || isNoData ? (
                <div className="text-center mt-5 text-muted">
                  <p className="fs-5">No data found</p>
                </div>
              ) : loading ? (
                <div className="d-flex justify-content-center">
                  <div
                    className="spinner-border center text-primary"
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                // Export Buttons
                <div>
                  <div className="d-flex justify-content-end mb-3">
                    <button
                      className="btn btn-outline-success me-2"
                      onClick={() =>
                        exportToExcel(
                          tableData,
                          title,
                          "tncrp_visualizationdata",
                        )
                      }
                    >
                      <i className="bi bi-file-earmark-excel me-1"></i>
                    </button>

                    <button
                      className="btn btn-outline-primary me-2"
                      onClick={() => exportToCSV(tableData)}
                      title="Export as CSV"
                    >
                      <i className="bi bi-file-earmark-text"></i>
                    </button>

                    {/* <button
                      className="btn btn-outline-danger"
                      onClick={() => PDFExport(tableData, columns)}
                      title="Export as PDF"
                    >
                      <i className="bi bi-file-earmark-pdf"></i>
                    </button> */}
                  </div>
                  <DataTable
                    columns={columns}
                    data={tableData}
                    customStyles={customStyles}
                    fixedHeader // freezes the header
                    fixedHeaderScrollHeight="600px" //table body scroll area
                    pagination
                    paginationPerPage={100}
                    highlightOnHover
                    dense
                    striped
                    responsive
                  ></DataTable>
                  {footNote && <p>{footNote}</p>}
                </div>
              )}
            </div>
          )}
          {selectedChart === "bar" && (
            <>
              {hasValidationError || isNoData ? (
                <div className="text-center mt-5 text-muted">
                  <p className="fs-5">No data found</p>
                </div>
              ) : loading ? (
                <div className="d-flex justify-content-center">
                  <div
                    className="spinner-border center text-primary"
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                <OvertimeBarchart
                  chartdata={chartData}
                  displaychart={selectedGenders}
                  selectedRate={selectedRate}
                />
              )}
            </>
          )}
          {selectedChart === "line" && (
            <>
              {hasValidationError || isNoData ? (
                <div className="text-center mt-5 text-muted">
                  <p className="fs-5">No data found</p>
                </div>
              ) : loading ? (
                <div className="d-flex justify-content-center">
                  <div
                    className="spinner-border center text-primary"
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                </div>
              ) : (
                <OvertimeLinechart
                  chartdata={chartData}
                  displaychart={selectedGenders}
                  selectedRate={selectedRate}
                />
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CancerOvertime;
